import type { BusinessCustomerPayload } from 'types/customers'
import type { AddBusinessCustomerV2FormData } from './types'

type TranslationFn = (key: string) => string

export type ConfirmItem = { id: string; title: string; body: string }

export const getConfirmItems = (
  t: TranslationFn,
  data: BusinessCustomerPayload,
  displayData: Partial<AddBusinessCustomerV2FormData>
): ConfirmItem[] => {
  const { company } = data
  const showOperationalAddress = company.isOperationalAddressDifferent

  return [
    { id: 'name', title: t('customers.businessName'), body: company.name },
    {
      id: 'entityType',
      title: t('manageBeneficiaries.entityType'),
      body: displayData.entityType || company.entityType,
    },
    {
      id: 'useCase',
      title: t('customers.useCase'),
      body: displayData.useCase || data.useCase || '',
    },
    {
      id: 'registrationNumber',
      title: t('customers.registrationNumber'),
      body: company.registrationNumber,
    },
    {
      id: 'taxNumber',
      title: t('customers.taxIdentification'),
      body: company.taxIdentification.number,
    },
    {
      id: 'taxResidenceCountryCode',
      title: t('customers.taxResidency'),
      body:
        displayData.taxResidenceCountryCode || company.taxIdentification.taxResidenceCountryCode,
    },
    {
      id: 'naicsCode',
      title: t('customers.naicsCode'),
      body: company.businessProfile.naicsCode,
    },
    {
      id: 'monthlyExpectedVolumes',
      title: t('customers.monthlyVolumes'),
      body: displayData.monthlyExpectedVolumes || company.businessProfile.monthlyExpectedVolumes,
    },
    {
      id: 'isRegulated',
      title: t('embeddedCustomers.businessForm.isRegulated'),
      body: displayData.isRegulated || (company.businessProfile.isRegulated ? t('yes') : t('no')),
    },
    ...(company.businessProfile.regulatorInformation
      ? [
          {
            id: 'regulatorName',
            title: t('customers.regulatorName'),
            body: company.businessProfile.regulatorInformation.regulatorName,
          },
          {
            id: 'regulatorJurisdiction',
            title: t('customers.regulatorJurisdiction'),
            body: company.businessProfile.regulatorInformation.regulatorJurisdiction,
          },
        ]
      : []),
    {
      id: 'sourceOfFunds',
      title: t('customers.sourceOfFunds'),
      body: displayData.sourceOfFunds || company.businessProfile.sourceOfFunds,
    },
    {
      id: 'intendedUseOfAccount',
      title: t('customers.intendedUse'),
      body: displayData.intendedUseOfAccount || company.businessProfile.intendedUseOfAccount,
    },
    {
      id: 'website',
      title: t('customers.website'),
      body: company.businessProfile.website,
    },
    {
      id: 'registeredAddressLine1',
      title: t('customers.address.addressLine1'),
      body: company.address.addressLine1,
    },
    {
      id: 'registeredAddressCity',
      title: t('customers.address.city'),
      body: company.address.city,
    },
    {
      id: 'registeredAddressPostalCode',
      title: t('customers.address.postalCode'),
      body: company.address.postalCode,
    },
    {
      id: 'registeredAddressCountryCode',
      title: t('customers.address.country'),
      body: displayData.registeredAddressCountryCode || company.address.countryCode,
    },
    {
      id: 'isOperationalAddressDifferent',
      title: t('customers.isOperationalAddressDifferent'),
      body:
        displayData.isOperationalAddressDifferent ||
        (company.isOperationalAddressDifferent ? t('yes') : t('no')),
    },
    ...(showOperationalAddress && company.operationalAddress
      ? [
          {
            id: 'operationalAddressLine1',
            title: t('customers.address.addressLine1'),
            body: company.operationalAddress.addressLine1,
          },
          {
            id: 'operationalAddressCity',
            title: t('customers.address.city'),
            body: company.operationalAddress.city,
          },
          {
            id: 'operationalAddressPostalCode',
            title: t('customers.address.postalCode'),
            body: company.operationalAddress.postalCode,
          },
          {
            id: 'operationalAddressCountryCode',
            title: t('customers.address.country'),
            body:
              displayData.operationalAddressCountryCode || company.operationalAddress.countryCode,
          },
        ]
      : []),
  ]
}
